import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollTo = (href: string) => {
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-[#F0F0EE] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Main grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-gray-200">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src="https://static.readdy.ai/image/577ebfb2b9cf841907632e366e1dd697/1697ba7e5f2a4d4ef72f246478d823d0.png"
              alt="Ferrarezi Benefícios"
              className="h-9 w-auto object-contain brightness-0 mb-5"
            />
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs mb-5">
              Aceleradora de pessoas e resultados. Benefícios, seguros e estratégia com dados para redução de custos, retenção de talentos e produtividade real.
            </p>
            {/* Social */}
            <div className="flex items-center gap-3">
              {[
                { icon: "ri-instagram-line", href: "https://www.instagram.com/ferrarezibeneficios/" },
                { icon: "ri-linkedin-fill", href: "https://www.linkedin.com/company/ferrarezi-consultoria-de-beneficios/" },
                { icon: "ri-facebook-fill", href: "https://www.facebook.com/FerrareziBeneficios" },
              ].map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-black text-white hover:bg-[#FBC52D] hover:text-black transition-all duration-200 cursor-pointer"
                >
                  <i className={`${social.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-black font-bold text-xs tracking-widest uppercase mb-5">Soluções</h4>
            <ul className="flex flex-col gap-3">
              {[
                { label: "Benefícios Corporativos", href: "/servicos/beneficios-corporativos" },
                { label: "Plano de Saúde", href: "/servicos/plano-de-saude" },
                { label: "Plano Odontológico", href: "/servicos/plano-odontologico" },
                { label: "Consultoria Estratégica", href: "/servicos/consultoria-estrategica" },
                { label: "Hub de Seguros", href: "/servicos/hub-de-seguros" },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-gray-500 text-sm hover:text-[#FBC52D] transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-black font-bold text-xs tracking-widest uppercase mb-5">Seguros</h4>
            <ul className="flex flex-col gap-3">
              {[
                "Saúde Empresarial",
                "Vida em Grupo",
                "Odontológico",
                "Empresarial",
                "Cyber",
                "D&O",
              ].map((item) => (
                <li key={item}>
                  <a
                    href="#hub-seguros"
                    onClick={(e) => { e.preventDefault(); scrollTo("#hub-seguros"); }}
                    className="text-gray-500 text-sm hover:text-[#FBC52D] transition-colors cursor-pointer"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-black font-bold text-xs tracking-widest uppercase mb-5">Contato</h4>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-2.5">
                <i className="ri-map-pin-line text-[#FBC52D] text-sm mt-0.5" />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Rua+Juventus+193+Parque+da+Mooca+São+Paulo+SP+03124-020"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 text-sm leading-relaxed hover:text-[#FBC52D] transition-colors cursor-pointer"
                >
                  Rua Juventus, 193 - Parque da Mooca<br />
                  São Paulo - SP, 03124-020
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-phone-line text-[#FBC52D] text-sm" />
                <a href="tel:+5511936189998" className="text-gray-500 text-sm hover:text-[#FBC52D] transition-colors">
                  +55 11 93618-9998
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-mail-line text-[#FBC52D] text-sm" />
                <a href="mailto:contato@ferrarezi.com.br" className="text-gray-500 text-sm hover:text-[#FBC52D] transition-colors break-all">
                  contato@ferrarezi.com.br
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-whatsapp-line text-[#FBC52D] text-sm" />
                <a href="https://wa.me/5511936189998" target="_blank" rel="noopener noreferrer" className="text-gray-500 text-sm hover:text-[#FBC52D] transition-colors">
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col items-center pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
            <p className="text-gray-400 text-xs">
              &copy; {new Date().getFullYear()} Ferrarezi, Aceleradora de Pessoas e Resultados. Todos os direitos reservados.
            </p>
            <Link to="/politica-de-privacidade" className="text-gray-400 text-xs hover:text-gray-600 transition-colors cursor-pointer">
              Política de Privacidade
            </Link>
          </div>
          <p className="text-gray-300 text-[11px] mt-6">
            Desenvolvido por{' '}
            <a href="https://adigitalmarketing.com.br/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-500 transition-colors cursor-pointer">
              A Digital Marketing
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}